import { useState, useEffect, useCallback, useMemo, lazy, Suspense } from 'react';
import { formatDistanceToNow, isValid } from 'date-fns';
import { enUS } from 'date-fns/locale';
import { supabase } from './supabaseClient';
import './App.css';

import Modal from 'react-modal';
import Spinner from './components/Spinner';
import Header from './components/Header';
import TimeControl from './components/TimeControl';
import HomeStrip from './components/HomeStrip';
import OfflineBanner from './components/OfflineBanner';
import { PALETTES, DEFAULT_PALETTE, deviceMarkerColor } from './palettes';

// Heavy / on-demand chunks. The initial bundle now only ships the
// header / time-control shell. Leaflet (~150 KB) only loads when the
// map view actually mounts; chart.js (~250 KB) when the history modal
// opens; admin / payload / table panels are gated similarly.
const MapView       = lazy(() => import('./components/MapView'));
const StationsTable = lazy(() => import('./components/StationsTable'));
const StationPage   = lazy(() => import('./components/StationPage'));
const DataPage          = lazy(() => import('./components/DataPage'));
const BboxPanel         = lazy(() => import('./components/BboxPanel'));
const RegistrationForm  = lazy(() => import('./components/RegistrationForm'));
const ObserveForm       = lazy(() => import('./components/ObserveForm'));
const DeviceModal   = lazy(() => import('./components/DeviceModal'));
const AdminPanel    = lazy(() => import('./components/AdminPanel'));
const PayloadModal  = lazy(() => import('./components/PayloadModal'));

Modal.setAppElement('#root');

const API_URL = process.env.REACT_APP_API_URL || 'http://127.0.0.1:8000';
const DEVICE_REFRESH_INTERVAL = 5 * 60 * 1000;
const STATUS_REFRESH_INTERVAL = 60 * 1000;

// Marker colour can be one of three palettes (raw / mpsas / bortle).
// Selection lives in URL state and is plumbed through to MapView and
// MapLegend; see palettes.js for the actual band definitions.
const DEFAULT_THRESHOLDS = { night_max: 100, twilight_max: 10000 };

// Read current URL search params (window.location is the source of truth at mount).
const readUrlState = () => {
  if (typeof window === 'undefined') return {};
  const p = new URLSearchParams(window.location.search);
  const view = p.get('view');
  const palette = p.get('palette');
  const validViews = new Set(['map', 'table', 'station', 'data', 'register', 'observe']);
  // ?bbox=west,south,east,north (decimal degrees)
  let bbox = null;
  const bboxStr = p.get('bbox');
  if (bboxStr) {
    const nums = bboxStr.split(',').map(Number);
    if (nums.length === 4 && nums.every((n) => Number.isFinite(n))) bbox = nums;
  }
  return {
    view: validViews.has(view) ? view : 'map',
    asOf: p.get('as_of') || null,
    device: p.get('device') || null,
    palette: PALETTES.includes(palette) ? palette : DEFAULT_PALETTE,
    bbox,
  };
};

function App() {
  const initialUrl = readUrlState();
  const [session, setSession] = useState(null);
  const [devices, setDevices] = useState([]);
  const [devicesLoading, setDevicesLoading] = useState(true);
  const [devicesError, setDevicesError] = useState(null);
  const [modalIsOpen, setModalIsOpen] = useState(false);
  const [selectedDevice, setSelectedDevice] = useState(null);
  const [chartData, setChartData] = useState(null);
  const [mapCenter, setMapCenter] = useState([39.0742, 21.8243]);
  const [mapZoom, setMapZoom] = useState(7);
  const [networkStats, setNetworkStats] = useState({ stationCount: 0, averageRaw: null });
  const [lastMessageTime, setLastMessageTime] = useState(null);
  const [staleCount, setStaleCount] = useState(0);
  const [view, setView] = useState(initialUrl.view); // 'map' | 'table' | 'station'
  const [pendingDeviceId, setPendingDeviceId] = useState(initialUrl.device);
  const [palette, setPalette] = useState(initialUrl.palette);
  const [bbox, setBbox] = useState(initialUrl.bbox);
  const [observations, setObservations] = useState([]);

  // Pull naked-eye observations for the map layer (lightweight, public).
  const fetchObservations = useCallback(() => {
    fetch(`${API_URL}/observations?days=30&limit=500`)
      .then((res) => (res.ok ? res.json() : []))
      .then(setObservations)
      .catch(() => setObservations([]));
  }, []);
  useEffect(() => {
    fetchObservations();
    const id = setInterval(fetchObservations, 5 * 60 * 1000);
    return () => clearInterval(id);
  }, [fetchObservations]);
  const [stationStats, setStationStats] = useState([]);
  const [statsLoading, setStatsLoading] = useState(false);
  const [statsError, setStatsError] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [adminOpen, setAdminOpen] = useState(false);
  const [thresholds, setThresholds] = useState(DEFAULT_THRESHOLDS);
  const [payloadDevice, setPayloadDevice] = useState(null);
  const [asOf, setAsOf] = useState(initialUrl.asOf); // null = live; ISO string = point-in-time

  const openPayload = useCallback((device) => setPayloadDevice(device), []);
  const closePayload = useCallback(() => setPayloadDevice(null), []);

  const fetchThresholds = useCallback(() => {
    fetch(`${API_URL}/config/light-thresholds`)
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => { if (data) setThresholds(data); })
      .catch(() => { /* keep defaults */ });
  }, []);

  useEffect(() => { fetchThresholds(); }, [fetchThresholds]);

  // --- URL <-> state sync ---------------------------------------------
  // Sync (view, as_of, device) into the URL whenever they change.
  // Use replaceState so frequent updates (e.g. scrubbing as_of) don't
  // pollute the back-button history.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const p = new URLSearchParams();
    if (view !== 'map') p.set('view', view);
    if (asOf) p.set('as_of', asOf);
    // Station view always carries ?device=<id>; the modal carries it too while open.
    if (view === 'station' && pendingDeviceId) {
      p.set('device', pendingDeviceId);
    } else if (modalIsOpen && selectedDevice?.device_id) {
      p.set('device', selectedDevice.device_id);
    }
    if (palette !== DEFAULT_PALETTE) p.set('palette', palette);
    if (bbox && bbox.length === 4) p.set('bbox', bbox.map((n) => n.toFixed(4)).join(','));
    const qs = p.toString();
    const target = qs ? `${window.location.pathname}?${qs}` : window.location.pathname;
    if (window.location.pathname + window.location.search !== target) {
      window.history.replaceState(null, '', target);
    }
  }, [view, asOf, modalIsOpen, selectedDevice, palette, pendingDeviceId, bbox]);

  // Browser back/forward — re-read URL and restore state.
  useEffect(() => {
    const onPop = () => {
      const next = readUrlState();
      setView(next.view);
      setAsOf(next.asOf);
      setPalette(next.palette);
      setBbox(next.bbox);
      if (next.device) setPendingDeviceId(next.device);
      else setModalIsOpen(false);
    };
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);

  // Auth
  useEffect(() => {
    if (!supabase) return;
    supabase.auth.getSession().then(({ data: { session: s } }) => setSession(s));
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, s) => setSession(s));
    return () => subscription.unsubscribe();
  }, []);

  // Check admin status when session changes
  useEffect(() => {
    if (!session?.access_token) {
      setIsAdmin(false);
      return;
    }
    fetch(`${API_URL}/admin/me`, {
      headers: { Authorization: `Bearer ${session.access_token}` },
    })
      .then((res) => setIsAdmin(res.ok))
      .catch(() => setIsAdmin(false));
  }, [session]);

  // Fetch devices (optionally as-of a past timestamp)
  const fetchDevices = useCallback(() => {
    setDevicesError(null);
    const url = asOf
      ? `${API_URL}/devices?as_of=${encodeURIComponent(asOf)}`
      : `${API_URL}/devices`;
    fetch(url)
      .then(res => { if (!res.ok) throw new Error(`Server error (${res.status})`); return res.json(); })
      .then(data => {
        setDevices(data);
        setDevicesLoading(false);
        if (data.length > 0) {
          const withRaw = data.filter(d => d.light_value !== null && d.light_value !== undefined);
          const avgRaw = withRaw.length > 0
            ? withRaw.reduce((s, d) => s + d.light_value, 0) / withRaw.length
            : null;
          setNetworkStats({ stationCount: data.length, averageRaw: avgRaw });
          const located = data.filter(d => d.latitude != null && d.longitude != null);
          if (located.length > 0) {
            const lats = located.map(d => d.latitude).sort((a, b) => a - b);
            const lons = located.map(d => d.longitude).sort((a, b) => a - b);
            setMapCenter([lats[Math.floor(lats.length / 2)], lons[Math.floor(lons.length / 2)]]);
          }
        } else {
          setNetworkStats({ stationCount: 0, averageRaw: null });
        }
      })
      .catch(err => {
        console.error("Failed to fetch devices:", err);
        setDevicesLoading(false);
        setDevicesError(err.message);
      });
  }, [asOf]);

  useEffect(() => {
    fetchDevices();
    // Auto-refresh only in live mode; pinned past timestamp shouldn't change.
    if (asOf) return;
    const id = setInterval(fetchDevices, DEVICE_REFRESH_INTERVAL);
    return () => clearInterval(id);
  }, [fetchDevices, asOf]);

  // Fetch network status
  useEffect(() => {
    const fetchStatus = () => {
      fetch(`${API_URL}/status`)
        .then(res => { if (!res.ok) throw new Error(`HTTP ${res.status}`); return res.json(); })
        .then(data => {
          setLastMessageTime(data.last_message_received_at);
          setStaleCount(data.stations_stale ?? 0);
        })
        .catch(err => console.error("Failed to fetch network status:", err));
    };
    fetchStatus();
    const id = setInterval(fetchStatus, STATUS_REFRESH_INTERVAL);
    return () => clearInterval(id);
  }, []);

  // Fetch device history
  const fetchHistory = useCallback((deviceId) => {
    setChartData(null);
    fetch(`${API_URL}/devices/${encodeURIComponent(deviceId)}/history`)
      .then(res => { if (!res.ok) throw new Error(`HTTP ${res.status}`); return res.json(); })
      .then(history => {
        const labels = history.map(d => new Date(d.device_timestamp).toLocaleTimeString('en-US'));
        setChartData({
          labels,
          datasets: [
            {
              label: 'Light (raw)',
              data: history.map(d => d.light_value),
              borderColor: '#ffc107',
              yAxisID: 'y',
              pointBackgroundColor: '#ffc107',
              tension: 0.1,
              borderDash: [],
            },
            {
              label: 'Temperature (\u00B0C)',
              data: history.map(d => d.temperature),
              borderColor: '#dc3545',
              yAxisID: 'y1',
              pointBackgroundColor: '#dc3545',
              tension: 0.1,
              borderDash: [5, 5],
            },
          ],
        });
      })
      .catch(err => {
        console.error(`Failed to fetch history for ${deviceId}:`, err);
        setChartData('error');
      });
  }, []);

  const openModal = useCallback((device) => {
    setSelectedDevice(device);
    fetchHistory(device.device_id);
    setModalIsOpen(true);
  }, [fetchHistory]);

  // Resolve ?device=<id> deep-link once devices have loaded.
  // In station view we KEEP pendingDeviceId set (it's what selects the
  // station to render); only the legacy "open history modal" deep-link
  // path consumes it.
  useEffect(() => {
    if (view === 'station') return;
    if (!pendingDeviceId || devices.length === 0) return;
    const found = devices.find(d => d.device_id === pendingDeviceId);
    if (found) {
      openModal(found);
      if (found.latitude != null && found.longitude != null) {
        setMapCenter([found.latitude, found.longitude]);
        setMapZoom(z => Math.max(z, 12));
      }
    }
    setPendingDeviceId(null);
  }, [pendingDeviceId, devices, openModal, view]);

  const closeModal = useCallback(() => setModalIsOpen(false), []);

  // Fetch station stats (honours as-of filter for the table view)
  const fetchStats = useCallback(() => {
    setStatsError(null);
    setStatsLoading(true);
    const url = asOf
      ? `${API_URL}/devices/stats?as_of=${encodeURIComponent(asOf)}`
      : `${API_URL}/devices/stats`;
    fetch(url)
      .then(res => { if (!res.ok) throw new Error(`Server error (${res.status})`); return res.json(); })
      .then(data => { setStationStats(data); setStatsLoading(false); })
      .catch(err => { console.error("Failed to fetch station stats:", err); setStatsLoading(false); setStatsError(err.message); });
  }, [asOf]);

  const switchView = useCallback((v) => {
    setView(v);
    if (v === 'table') fetchStats();
  }, [fetchStats]);

  // Re-fetch stats when as_of changes while table view is active
  useEffect(() => {
    if (view === 'table') fetchStats();
  }, [asOf, view, fetchStats]);

  const handleLocateDevice = useCallback((device) => {
    setMapCenter([device.latitude, device.longitude]);
    setMapZoom(14);
    setView('map');
  }, []);

  const chartOptions = useMemo(() => ({
    responsive: true,
    maintainAspectRatio: false,
    color: '#f0f0f0',
    scales: {
      y: {
        position: 'left',
        title: { display: true, text: 'Light (raw counts)', color: '#f0f0f0' },
        ticks: { color: '#f0f0f0' },
        grid: { color: 'rgba(255, 255, 255, 0.1)' },
      },
      y1: {
        position: 'right',
        title: { display: true, text: 'Temperature (\u00B0C)', color: '#f0f0f0' },
        ticks: { color: '#f0f0f0' },
        grid: { drawOnChartArea: false },
      },
      x: {
        title: { display: true, text: 'Time (Last 24 Hours)', color: '#f0f0f0' },
        ticks: { color: '#f0f0f0', maxRotation: 45 },
        grid: { color: 'rgba(255, 255, 255, 0.1)' },
      },
    },
    plugins: {
      legend: {
        labels: {
          color: '#f0f0f0',
          usePointStyle: true,
        },
      },
      tooltip: {
        titleColor: '#00ffff',
        bodyColor: '#f0f0f0',
        backgroundColor: 'rgba(10, 25, 47, 0.9)',
        borderColor: 'rgba(0, 255, 255, 0.3)',
        borderWidth: 1,
      },
    },
  }), []);

  const formatDeviceTime = (timeStr) => {
    try {
      const d = new Date(timeStr);
      return isValid(d) ? formatDistanceToNow(d, { addSuffix: true, locale: enUS }) : 'Unknown';
    } catch {
      return 'Unknown';
    }
  };

  return (
    <div className="app-container">
      <Header
        stats={networkStats}
        lastMessageTime={lastMessageTime}
        staleCount={staleCount}
        session={session}
        view={view}
        onViewChange={switchView}
        isAdmin={isAdmin}
        onOpenAdmin={() => setAdminOpen(true)}
        onOpenData={() => setView('data')}
        onOpenRegister={() => setView('register')}
        onOpenObserve={() => setView('observe')}
      />
      <OfflineBanner lastDataAt={lastMessageTime} />
      <HomeStrip
        stationCount={networkStats.stationCount}
        readingCount={stationStats.reduce((s, d) => s + (d.reading_count || 0), 0)}
        onOpenData={() => setView('data')}
      />
      {view !== 'data' && view !== 'station' && <TimeControl asOf={asOf} onChange={setAsOf} />}
      {view === 'register' ? (
        <Suspense fallback={<div style={{padding:'24px',textAlign:'center'}}><Spinner /></div>}>
          <RegistrationForm onClose={() => switchView('map')} />
        </Suspense>
      ) : view === 'observe' ? (
        <Suspense fallback={<div style={{padding:'24px',textAlign:'center'}}><Spinner /></div>}>
          <ObserveForm onClose={() => { switchView('map'); fetchObservations(); }} />
        </Suspense>
      ) : view === 'data' ? (
        <Suspense fallback={<div style={{padding:'24px',textAlign:'center'}}><Spinner /></div>}>
          <DataPage
            onClose={() => switchView('map')}
            stationCount={networkStats.stationCount}
            readingCount={stationStats.reduce((s, d) => s + (d.reading_count || 0), 0)}
          />
        </Suspense>
      ) : view === 'station' && pendingDeviceId ? (
        <Suspense fallback={<div style={{padding:'24px',textAlign:'center'}}><Spinner /></div>}>
          <StationPage
            deviceId={pendingDeviceId}
            onClose={() => { setPendingDeviceId(null); switchView('map'); }}
            onLocate={(d) => {
              handleLocateDevice(d);
              setPendingDeviceId(null);
            }}
          />
        </Suspense>
      ) : view === 'table' ? (
        <Suspense fallback={<div style={{padding:'24px',textAlign:'center'}}><Spinner /></div>}>
          <StationsTable
            stats={stationStats}
            loading={statsLoading}
            error={statsError}
            onRetry={fetchStats}
            onViewHistory={(d) => { openModal(d); }}
            onLocateDevice={handleLocateDevice}
            onViewPayload={openPayload}
            onOpenStation={(deviceId) => {
              setPendingDeviceId(deviceId);
              setView('station');
            }}
          />
        </Suspense>
      ) : (
        <Suspense fallback={<div style={{padding:'24px',textAlign:'center'}}><Spinner /></div>}>
          <MapView
            devices={devices}
            mapCenter={mapCenter}
            mapZoom={mapZoom}
            thresholds={thresholds}
            palette={palette}
            onPaletteChange={setPalette}
            formatDeviceTime={formatDeviceTime}
            onSelectDevice={openModal}
            onViewPayload={openPayload}
            onSwitchToTable={() => switchView('table')}
            loading={devicesLoading}
            error={devicesError}
            onRetry={fetchDevices}
            onOpenStation={(deviceId) => {
              setPendingDeviceId(deviceId);
              setView('station');
            }}
            bbox={bbox}
            onBboxSelected={setBbox}
            observations={observations}
            onOpenObserve={() => setView('observe')}
          />
        </Suspense>
      )}

      <Suspense fallback={null}>
        {modalIsOpen && (
          <DeviceModal
            isOpen={modalIsOpen}
            onClose={closeModal}
            device={selectedDevice}
            chartData={chartData}
            chartOptions={chartOptions}
            onRetry={() => selectedDevice && fetchHistory(selectedDevice.device_id)}
          />
        )}
        {adminOpen && (
          <AdminPanel
            isOpen={adminOpen}
            onClose={() => setAdminOpen(false)}
            accessToken={session?.access_token}
            thresholds={thresholds}
            onThresholdsSaved={(t) => setThresholds(t)}
          />
        )}
        {payloadDevice !== null && (
          <PayloadModal
            isOpen={payloadDevice !== null}
            onClose={closePayload}
            device={payloadDevice}
          />
        )}
        {bbox && view === 'map' && (
          <BboxPanel bbox={bbox} onClose={() => setBbox(null)} palette={palette} />
        )}
      </Suspense>
    </div>
  );
}

export default App;
